import { useAtom } from 'jotai';
import { useCallback, useState } from 'react';
import { useDisclosure } from '@heroui/react';
import { toast } from 'react-toastify';

import { createPlan, deletePlan, updatePlan } from '@/store';
import type { Plan, PlanPayload } from '@/types';

const createEmptyForm = (): PlanPayload => ({
  name: '',
  slug: '',
  price: 0,
  currency: 'USD',
  credit_amount: 1,
  description: '',
  features: [],
  sort_order: 0,
  is_active: true
});

export function usePlanForm() {
  const [, createPlanFn] = useAtom(createPlan);
  const [, updatePlanFn] = useAtom(updatePlan);
  const { isOpen, onOpen, onOpenChange } = useDisclosure();
  const [editingPlan, setEditingPlan] = useState<Plan | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState<PlanPayload>(createEmptyForm());

  const openCreate = useCallback(() => {
    setEditingPlan(null);
    setFormData(createEmptyForm());
    onOpen();
  }, [onOpen]);

  const openEdit = useCallback(
    (plan: Plan) => {
      setEditingPlan(plan);
      setFormData({
        name: plan.name,
        slug: plan.slug,
        price: plan.price,
        currency: plan.currency ?? 'USD',
        credit_amount: plan.credit_amount,
        description: plan.description ?? '',
        features: Array.isArray(plan.features) ? [...plan.features] : [],
        sort_order: plan.sort_order ?? 0,
        is_active: plan.is_active ?? true
      });
      onOpen();
    },
    [onOpen]
  );

  const savePlan = useCallback(async () => {
    if (!formData.name.trim()) {
      toast.warning('Plan name is required');
      return;
    }
    if (!formData.slug.trim()) {
      toast.warning('Slug is required');
      return;
    }

    try {
      setIsSubmitting(true);
      const payload = {
        ...formData,
        price: typeof formData.price === 'string' ? parseFloat(formData.price) || 0 : formData.price,
        credit_amount: Number(formData.credit_amount) || 1,
        sort_order: Number(formData.sort_order) || 0
      };
      if (editingPlan) {
        await updatePlanFn({ id: editingPlan.id, payload });
        toast.success('Plan updated');
      } else {
        await createPlanFn(payload);
        toast.success('Plan created');
      }
      onOpenChange();
      setEditingPlan(null);
      setFormData(createEmptyForm());
    } catch (error) {
      // handled globally
    } finally {
      setIsSubmitting(false);
    }
  }, [createPlanFn, updatePlanFn, editingPlan, formData, onOpenChange]);

  return {
    isOpen,
    onOpen,
    onOpenChange,
    openCreate,
    openEdit,
    savePlan,
    isSubmitting,
    isEditMode: Boolean(editingPlan),
    formData,
    setFormData
  };
}

export function usePlanDelete() {
  const [, deletePlanFn] = useAtom(deletePlan);
  const { isOpen, onOpen, onOpenChange } = useDisclosure();
  const [pendingPlan, setPendingPlan] = useState<Plan | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const openDelete = useCallback(
    (plan: Plan) => {
      setPendingPlan(plan);
      onOpen();
    },
    [onOpen]
  );

  const confirmDelete = useCallback(async () => {
    if (!pendingPlan) return;
    try {
      setIsDeleting(true);
      await deletePlanFn(pendingPlan.id);
      toast.success('Plan deleted');
      onOpenChange();
      setPendingPlan(null);
    } catch (error) {
      // handled globally
    } finally {
      setIsDeleting(false);
    }
  }, [deletePlanFn, onOpenChange, pendingPlan]);

  return {
    isOpen,
    onOpen,
    onOpenChange,
    openDelete,
    confirmDelete,
    isDeleting,
    pendingPlan
  };
}
